import React from 'react'
import { Helmet } from 'react-helmet'

/**
 * SchemaMarkup component for implementing schema.org structured data
 *
 * @param {Object} props - Component props
 * @param {string} props.type - Schema type to render (WebSite, SearchAction, SearchResultsPage, ItemList, HowTo, FAQPage, Question, Dataset, Service, LocalBusiness)
 * @param {Object} props.data - Data for the schema
 * @returns {JSX.Element} - Helmet component with JSON-LD script
 */
const SchemaMarkup = ({ type, data }) => {
  // Return null if no type or data is provided
  if (!type || !data) return null

  let schema = {}

  switch (type) {
    case 'WebSite':
      schema = {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name:
          data.name ||
          'Conversor de Códigos IAE y CNAE para Autónomos y Empresas',
        url: data.url || 'https://conversoriaecnae.es',
        description:
          data.description ||
          'Herramienta para convertir epígrafes IAE a códigos CNAE 2025 y viceversa. Encuentra la equivalencia entre códigos de actividades económicas actualizados.',
        potentialAction: {
          '@type': 'SearchAction',
          target: {
            '@type': 'EntryPoint',
            urlTemplate: `${
              data.url || 'https://conversoriaecnae.es'
            }?search={search_term_string}`,
          },
          'query-input': 'required name=search_term_string',
        },
      }
      break

    case 'SearchAction':
      schema = {
        '@context': 'https://schema.org',
        '@type': 'SearchAction',
        target: {
          '@type': 'EntryPoint',
          urlTemplate: `${
            data.url || 'https://conversoriaecnae.es'
          }?search={search_term_string}`,
        },
        'query-input': 'required name=search_term_string',
      }
      break

    case 'SearchResultsPage':
      schema = {
        '@context': 'https://schema.org',
        '@type': 'SearchResultsPage',
        mainEntity: {
          '@type': 'ItemList',
          itemListElement:
            data.results?.map((result, index) => ({
              '@type': 'ListItem',
              position: index + 1,
              item: {
                '@type': 'Thing',
                name: result.title,
                url: result.url,
              },
            })) || [],
        },
      }
      break

    case 'ItemList':
      schema = {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: data.name || 'Códigos CNAE y Epígrafes IAE Más Populares',
        description:
          data.description ||
          'Lista de los códigos CNAE y epígrafes IAE más buscados',
        numberOfItems: data.items?.length || 0,
        itemListElement:
          data.items?.map((item, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            item: {
              '@type': 'Thing',
              name: item.title,
              url: item.url,
              identifier: item.code,
            },
          })) || [],
      }
      break

    case 'HowTo':
      schema = {
        '@context': 'https://schema.org',
        '@type': 'HowTo',
        name: data.name || '¿Cómo Funciona el Conversor IAE CNAE?',
        description:
          data.description ||
          'Transforma instantáneamente tus epígrafes IAE a códigos CNAE 2025 y viceversa con nuestro conversor especializado.',
        step:
          data.steps?.map((step, index) => ({
            '@type': 'HowToStep',
            position: index + 1,
            name: step.title,
            text: step.description,
            image: step.image,
          })) || [],
      }
      break

    case 'FAQPage':
      schema = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity:
          data.questions?.map((question) => ({
            '@type': 'Question',
            name: question.question,
            acceptedAnswer: {
              '@type': 'Answer',
              text: question.answer,
            },
          })) || [],
      }
      break

    case 'Question':
      schema = {
        '@context': 'https://schema.org',
        '@type': 'Question',
        name: data.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: data.answer,
        },
      }
      break

    case 'Dataset':
      schema = {
        '@context': 'https://schema.org',
        '@type': 'Dataset',
        name: data.name,
        description: data.description,
        keywords: data.keywords,
        url: data.url,
        identifier: data.identifier,
        license: data.license || 'https://creativecommons.org/licenses/by/4.0/',
        creator: {
          '@type': 'Organization',
          name: data.creator || 'Conversor IAE CNAE',
        },
        datePublished: data.datePublished,
        dateModified: data.dateModified,
      }
      break

    case 'Service':
      schema = {
        '@context': 'https://schema.org',
        '@type': 'Service',
        name: data.name || 'Conversor de Códigos IAE y CNAE',
        description:
          data.description ||
          'Servicio de conversión entre epígrafes IAE y códigos CNAE para autónomos y empresas',
        provider: {
          '@type': 'Organization',
          name: data.provider || 'Conversor IAE CNAE',
          url: data.providerUrl || 'https://conversoriaecnae.es',
        },
        serviceType: data.serviceType || 'Conversión de códigos fiscales',
        areaServed: {
          '@type': 'Country',
          name: 'España',
        },
      }
      break

    case 'LocalBusiness':
      schema = {
        '@context': 'https://schema.org',
        '@type': 'LocalBusiness',
        name: data.name || 'Conversor IAE CNAE',
        description:
          data.description ||
          'Herramienta especializada para la conversión de códigos fiscales para empresas y autónomos en España',
        url: data.url || 'https://conversoriaecnae.es',
        telephone: data.telephone,
        email: data.email,
        address: {
          '@type': 'PostalAddress',
          addressCountry: 'ES',
          addressLocality: data.city || 'Madrid',
          addressRegion: data.region || 'Madrid',
          postalCode: data.postalCode,
        },
        openingHours: data.openingHours || 'Mo-Fr 09:00-18:00',
      }
      break

    default:
      return null
  }

  return (
    <Helmet>
      <script type='application/ld+json'>{JSON.stringify(schema)}</script>
    </Helmet>
  )
}

export default SchemaMarkup
